import React, { createRef, FC, ReactElement } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { clsx } from 'clsx';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Grow from '@mui/material/Grow';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { v4 as uuidv4 } from 'uuid';
// Components
import Text from '../text';
import SvgIcon from '../icons/SvgIcon';
import Separator from '../layout/separator';
// Hooks
import { getUndress } from '../../core/services/id';
// Resources
import { stringsRes } from '../../resources/strings';
import packageJson from '../../../package.json';
import { NavigationHeaderInfos } from './menuContent';
import { paths } from '../../navigation/routes/paths';
import { NavTopBarHeight } from '../../utils/constants';
import { useBreakpoints } from '../../customHooks/useBreakpoints';
import { getUser } from '../../localStorageAPI';
import { IconLogo } from '../../resources/images';
import { navigateTo, redirectToBackOffice } from '../../utils/redirect';
import AppearanceMenuItem from './appearanceMenuItem';

interface DesktopAppbarProps {
    onLogout: () => void;
    leftMenuEntries: NavigationHeaderInfos[];
    rightMenuEntries: NavigationHeaderInfos[];
    shouldRefresh?: boolean;
}

const DesktopAppbar: FC<DesktopAppbarProps> = (props): ReactElement => {
    const { onLogout, leftMenuEntries, rightMenuEntries, shouldRefresh = false } = props;
    const { isTabletSize } = useBreakpoints();
    const classes = useStyles();
    const location = useLocation();
    const navigate = useNavigate();
    const user = getUser();

    React.useEffect(() => {
        handleClose();
    }, [location]);

    const [anchorEl, setAnchorElReact] = React.useState(null);
    const [openIndex, setOpenIndex] = React.useState(null);
    const [logo, setLogo] = React.useState<IconLogo>(IconLogo.Logo);

    const onHover = (e, index): void => {
        e.stopPropagation();
        if (openIndex !== index) {
            setOpenIndex(null);
            setAnchorElReact(null);
            setTimeout(() => {
                const anchor = elRefs[index]?.current;
                if (anchor && document.body.contains(anchor)) {
                    setAnchorElReact(anchor);
                    setOpenIndex(index);
                }
            }, 1);
        }
    };

    // const onClickEvent = (e): void => {
    //     e.stopPropagation();
    //     setAnchorElReact(null);
    //     setOpenIndex(null);
    // };

    const handleClose = (): void => {
        setOpenIndex(null);
        setAnchorElReact(null);
    };

    const onAction = (e, action?): void => {
        handleClose();
        if (action) {
            action();
        }
    };

    const onDisconnect = () => {
        onLogout();
    };

    const onUndress = () => {
        getUndress().then(() => {
            // window.location.reload();
            redirectToBackOffice();
        });
    };

    const arrLength = rightMenuEntries.length;
    const [elRefs, setElRefs] = React.useState([]);

    React.useEffect(() => {
        // add or remove refs
        setElRefs((currentRefs) =>
            Array(arrLength)
                .fill(0)
                .map((_, i) => currentRefs[i] || createRef()),
        );
    }, [arrLength]);

    React.useEffect(() => {
        const newLogo =
            window.location.pathname.startsWith(paths.admin) || window.location.pathname.startsWith('/self_')
                ? IconLogo.LogoEntreprise
                : IconLogo.Logo;

        if (logo !== newLogo) {
            setLogo(newLogo);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [window.location.pathname]);

    const displayDesktop = (): JSX.Element => {
        return (
            <Toolbar className={classes.toolbar}>
                <div className={classes.leftSide}>
                    {renderLogo()}
                    {renderNavButtons(leftMenuEntries)}
                </div>
                <div className={classes.rightSide}>
                    {/* {renderNavButtons(rightMenuEntries)} */}
                    {renderNavHoverButtons(rightMenuEntries)}
                </div>
            </Toolbar>
        );
    };

    const renderLogo = (): JSX.Element => (
        <RouterLink to={paths.home} title={stringsRes.drawerMenu.home} className={classes.noDecoration}>
            <Tooltip title={`V ${packageJson.version}`}>
                <div
                    className={classes.logo}
                    onClick={() => {
                        if (shouldRefresh) {
                            navigate(0);
                        }
                    }}
                >
                    <SvgIcon icon={logo} size="inherit" stroke="none" />
                </div>
            </Tooltip>
        </RouterLink>
    );

    const renderNavButtons = (headers: NavigationHeaderInfos[]): JSX.Element[] => {
        const { pathname } = window.location;
        return headers.map((data: NavigationHeaderInfos): JSX.Element => {
            return (
                <Button
                    {...{
                        key: uuidv4(),
                        color: 'inherit',
                        // to: data.href,
                        onClick: () => {
                            navigateTo(navigate, data.href);
                            if (shouldRefresh) {
                                navigate(0);
                            }
                        },
                        component: RouterLink,
                        className: clsx(
                            classes.menuButton,
                            pathname === data.href && classes.selected,
                            data.backgroundColor && classes[data.backgroundColor],
                        ),
                        startIcon: data.icon,
                    }}
                >
                    {data.label}
                </Button>
            );
        });
    };

    const renderNavHoverButtons = (headers: NavigationHeaderInfos[]): JSX.Element[] => {
        const { pathname } = window.location;
        return headers.map((data: NavigationHeaderInfos, index: number): JSX.Element => {
            const isOpen = openIndex === index;
            const isSelected = pathname === data.href || pathname.startsWith(data.href);
            // Resolve the anchor live from the button ref instead of a stored DOM node.
            // A stored node becomes stale (detached) when the button remounts (e.g. its dynamic label changes), which makes MUI's Popper warn that the anchorEl is not part of the document layout.
            const anchorNode = elRefs[index]?.current ?? null;

            // const noSubMenu = {
            //     to: data.href,
            //     component: RouterLink,
            //     disableRipple: true,
            // };

            return (
                <div
                    key={`${data.label}${data.href}`}
                    onMouseLeave={() => {
                        handleClose();
                    }}
                >
                    <Button
                        ref={elRefs[index]}
                        aria-controls={isOpen ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        onMouseOver={(event) => onHover(event, index)}
                        color="inherit"
                        disableRipple
                        onClick={() => {
                            navigateTo(navigate, data.href);
                            if (shouldRefresh) {
                                navigate(0);
                            }
                        }}
                        // {...noSubMenu}
                        // {...(!data.menuItems && noSubMenu)} // disable main button click
                        className={clsx(
                            classes.menuButton,
                            openIndex === index && classes.opened,
                            isSelected && classes.selected,
                            isTabletSize && classes.menuButtonTablet,
                            data.backgroundColor && classes[data.backgroundColor],
                        )}
                        startIcon={data.icon}
                    >
                        {data.label}
                    </Button>
                    {data.menuItems && anchorEl && (
                        <div className={openIndex ? classes.visibility : classes.noVisibility}>
                            <Popper
                                open={isOpen && Boolean(anchorNode)}
                                anchorEl={anchorNode}
                                placement="bottom-end"
                                transition
                                className={classes.popperRoot}
                                modifiers={[
                                    { name: 'flip', enabled: false },
                                    {
                                        name: 'offset',
                                        enabled: true,
                                        options: {
                                            offset: [0, 0],
                                        },
                                    },
                                ]}
                            >
                                {({ TransitionProps, placement }) => (
                                    <Grow
                                        {...TransitionProps}
                                        style={{
                                            transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                                        }} // transitionProps already have a className, therefore it is better to use conditionnal inline style here
                                    >
                                        <Paper className={classes.paperContainer}>
                                            <MenuList
                                                autoFocusItem={isOpen}
                                                id="menu-list-grow"
                                                className={classes.menuList}
                                            >
                                                {data.menuItems.map(({ label, href, onClick, icon }) => {
                                                    let menuItem = (
                                                        <MenuItem
                                                            key={label}
                                                            onClick={(e) => onAction(e, onClick)}
                                                            className={classes.menuItem}
                                                        >
                                                            {icon && (
                                                                <ListItemIcon
                                                                    className={clsx(
                                                                        classes.listItemIcon,
                                                                        pathname === href &&
                                                                            classes.listItemIconSelected,
                                                                    )}
                                                                >
                                                                    {icon}
                                                                </ListItemIcon>
                                                            )}
                                                            <Text.ShortMediumText16
                                                                text={label}
                                                                className={classes.label}
                                                            />
                                                        </MenuItem>
                                                    );
                                                    if (href) {
                                                        menuItem = (
                                                            <div
                                                                key={label}
                                                                onClick={() => {
                                                                    handleClose();
                                                                    setTimeout(() => {
                                                                        navigateTo(navigate, href);
                                                                        if (shouldRefresh) {
                                                                            navigate(0);
                                                                        }
                                                                    }, 1);
                                                                }}
                                                                className={clsx(
                                                                    classes.link,
                                                                    pathname === href && classes.selected,
                                                                )}
                                                            >
                                                                {menuItem}
                                                            </div>
                                                        );
                                                    }
                                                    return menuItem;
                                                })}
                                                {data.href === paths.profile && (
                                                    <div>
                                                        <div className={classes.separator}>
                                                            <Separator height={1} customColor="menu" />
                                                        </div>
                                                        <AppearanceMenuItem
                                                            variant="menuItem"
                                                            className={classes.menuItem}
                                                            iconClassName={classes.listItemIcon}
                                                        />
                                                        {user?.puppeteer && (
                                                            <div>
                                                                <div className={classes.separator}>
                                                                    <Separator height={1} customColor="menu" />
                                                                </div>
                                                                <MenuItem
                                                                    onClick={(e) => onAction(e, onUndress)}
                                                                    className={classes.menuItem}
                                                                >
                                                                    <Text.ShortMediumText16
                                                                        customColor="grey"
                                                                        text={stringsRes.profile.undress}
                                                                    />
                                                                </MenuItem>
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div className={classes.separator}>
                                                                <Separator height={1} customColor="menu" />
                                                            </div>
                                                            <MenuItem
                                                                onClick={(e) => onAction(e, onDisconnect)}
                                                                className={classes.menuItem}
                                                            >
                                                                <Text.ShortMediumText16
                                                                    customColor="grey"
                                                                    text={stringsRes.profile.logout}
                                                                />
                                                            </MenuItem>
                                                        </div>
                                                    </div>
                                                )}
                                            </MenuList>
                                        </Paper>
                                    </Grow>
                                )}
                            </Popper>
                        </div>
                    )}
                </div>
            );
        });
    };

    return <>{displayDesktop()}</>;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        popperRoot: {
            display: 'flex',
            '& .MuiPaper-root': {
                boxShadow: 'rgb(0 0 0 / 4%) 0px 2px 1px 0px, rgb(0 0 0 / 10%) 0px 2px 10px',
            },
            zIndex: 2,
        },
        visibility: {
            visibility: 'visible',
        },
        noVisibility: {
            visibility: 'hidden',
        },
        leftSide: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
        },
        rightSide: {
            display: 'flex',
            height: '100%',
        },
        logo: {
            display: 'flex',
            '& svg > path': {
                fill: theme.palette.secondary.main,
            },
        },
        selected: {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.colors.background.selected,
        },
        opened: {
            backgroundColor: theme.palette.colors.background.selected,
        },
        menuList: {
            padding: 0,
            overflow: 'auto',
            maxHeight: 'calc(90vh - 60px)',
        },
        menuButton: {
            fontWeight: 500,
            size: '18px',
            marginLeft: '32px',
            padding: '18px 24px',
            height: '100%',
            borderRadius: 'unset',
            letterSpacing: '0.2px',
            '&:hover': {
                backgroundColor: theme.palette.colors.background.selected,
            },
        },
        menuButtonTablet: {
            marginLeft: theme.spacing(3),
        },
        link: {
            '&:hover': {
                textDecorationColor: theme.palette.secondary.light,
            },
        },
        menuItem: {
            padding: '16px 32px',
            '& .MuiListItemIcon-root': {
                minWidth: 'inherit',
            },
            '&:hover': {
                '& .MuiListItemIcon-root': {
                    color: 'unset',
                },
                // color: theme.palette.secondary.light, // Activate to change icon color on hover menu item
            },
            '&>div': {
                overflow: 'unset',
            },
        },
        label: {
            color: 'inherit',
        },
        listItemIcon: {
            minWidth: 18,
            marginRight: theme.spacing(2),
            color: theme.palette.colors.icon.primary,
        },
        listItemIconSelected: {
            color: theme.palette.secondary.main,
        },
        toolbar: {
            display: 'flex',
            flex: 1,
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: NavTopBarHeight,
            paddingBottom: 1,
        },
        noDecoration: {
            textDecoration: 'none',
        },
        paperContainer: {
            width: 232,
            marginTop: 10,
            backgroundColor: theme.palette.colors.background.appBar,
        },
        separator: {
            // Uncomment to adjust separator margins
            // marginLeft: 30,
            // marginRight: 20,
        },
        blue: {
            color: theme.palette.colors.text.accent,
            backgroundColor: theme.palette.colors.border,
            '& .MuiButton-startIcon': {
                alignSelf: 'end',
            },
        },
    }),
);

export default DesktopAppbar;
